<template>
  <BaseModal
    :is-open="true"
    :title="$t('stream.watchLive')"
    size="stream"
    hide-footer
    @close="close"
  >
    <div class="stream-content">
      <p v-if="!isAuthenticated" class="stream-message">
        {{ $t('stream.loginRequired') }}
      </p>
      <p v-else-if="error" class="stream-message">{{ displayError }}</p>
      <div v-else-if="isLoading" class="stream-message">
        {{ $t('stream.loading') }}
      </div>
      <div v-else-if="embedUrl" class="stream-ratio">
        <iframe
          class="livestream"
          :src="embedUrl"
          frameborder="0"
          allow="autoplay; fullscreen"
          allowfullscreen
        />
      </div>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import BaseModal from './BaseModal.vue'
import { useAuthStore } from '@/stores/auth'
import { useStreamStore } from '@/stores/stream'

const props = defineProps<{
  gameSlug: string
}>()

const emit = defineEmits<{
  (e: 'close'): void
}>()

const { t, te } = useI18n()
const authStore = useAuthStore()
const streamStore = useStreamStore()
const { isLoading, embedUrl, error } = storeToRefs(streamStore)

const isAuthenticated = computed(() => authStore.isAuthenticated)

const displayError = computed(() => {
  const key = error.value
  if (key && te(key)) {
    return t(key)
  }
  return key || t('stream.unavailable')
})

onMounted(() => {
  streamStore.clearStream()
  if (isAuthenticated.value) {
    streamStore.fetchStreamingVideo(props.gameSlug)
  }
})

onUnmounted(() => {
  streamStore.clearStream()
})

const close = () => {
  emit('close')
}
</script>

<script lang="ts">
export default {
  name: 'VideoLiveStream',
}
</script>

<style scoped>
.stream-content {
  width: 520px;
  max-width: 100%;
  margin: 0 auto;
}
.stream-ratio {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  min-height: 200px;
}
.stream-ratio .livestream {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0;
}
.stream-message {
  text-align: center;
  padding: 1.5rem 0;
  color: var(--text-primary);
}
</style>
